import { useQuery } from "@tanstack/react-query";
import { api } from "jiffy-api";
import { objNamesToCamelCase, objNamesToSnakeCase } from "common/utils/helpers/cases";
import type { ID, print } from "common/utils/models";

export type EstimatedDeliveryResult = {
  compositionDates: print.estimatedDelivery.CompositionDates | undefined;
  isOutOfStock: boolean;
};

const isFastEtaEnabled = () => window?.jiffy?.flip?.fastDeliveryEstimate === true;

const fetchViaFastEta = async (compositionId: ID): Promise<EstimatedDeliveryResult | undefined> => {
  const response = await api.product.fetchETA(
    { business_line: "print" },
    { composition_hash: JSON.stringify({ [compositionId]: 1 }), source: "pdp" }
  );
  const res = objNamesToCamelCase(response);

  if (!res?.success || !res?.estimates) return undefined;

  const estimate = res.estimates[String(compositionId)];

  if (!estimate) return undefined;

  const camelEstimate = objNamesToCamelCase(estimate);
  const shipment = {
    date: camelEstimate.date,
    dateWithDayName: camelEstimate.date,
    fullDate: camelEstimate.date,
    showLogo: false,
    items: 1
  };
  return {
    compositionDates: {
      showLogo: false,
      valueFull: camelEstimate.date,
      size: 1,
      timeleft: camelEstimate.timeleft || null,
      deliveryTime: camelEstimate.deliveryTime || null,
      deliveryTimeEnd: camelEstimate.deliveryTimeEnd || null,
      shipments: [shipment],
      onlyCartShipments: [shipment],
      shippingCostInfo: null
    } as unknown as print.estimatedDelivery.CompositionDates,
    isOutOfStock: false
  };
};

const fetchViaLegacy = async (compositionId: ID): Promise<EstimatedDeliveryResult | undefined> => {
  const requestParams: print.estimatedDelivery.Request = { compositionId };
  const res = await api.print
    .estimatedDelivery(objNamesToSnakeCase(requestParams))
    .then(objNamesToCamelCase);

  if (res && res.success) {
    return { compositionDates: res.compositionDates, isOutOfStock: false };
  }
  if (res && res.outOfStock) {
    return { compositionDates: undefined, isOutOfStock: true };
  }
  return undefined;
};

export function useEstimatedDelivery(compositionId?: ID) {
  return useQuery({
    enabled: !!compositionId,
    queryKey: ["estimatedDelivery", compositionId],
    queryFn: async (): Promise<EstimatedDeliveryResult | undefined> => {
      if (!compositionId) return undefined;

      if (isFastEtaEnabled()) {
        try {
          return await fetchViaFastEta(compositionId);
        } catch {
          return fetchViaLegacy(compositionId);
        }
      }

      return fetchViaLegacy(compositionId);
    }
  });
}
